import React from "react";
import InsightSurface from "../shared/InsightSurface";
import VerdictBlock from "../shared/VerdictBlock";
import { SkeletonAlert } from "../shared/Skeleton";
import { fmtCount, fmtCurrency } from "../shared/format";
import { RefreshingOverlay } from "../shared/PanelShell";
import FindingRow from "./FindingRow";

// KEY FINDINGS — Tier 1 (V6 §E.1). Four findings assembled from four already-fetched
// RPCs (the funnel and agent value), each an icon-led clickable line, inside an
// InsightSurface tone="findings". The verdict sits above it, FIRST and LARGEST
// (locked decision 3) via VerdictBlock variant="verdict", and is hidden entirely
// when absent (the "no-sentences-at-all" state — Key Findings still render because
// they come from RPCs, not sentences).
//
// ASSEMBLED, NOT COMPUTED. Every number here comes straight from a payload —
// dropped_here, a stage count, recoverable.count/value, after_hours.calls_answered —
// and the drop-label fragments are the backend's own words. Placing them in a line
// is presentation, the way the drain places a count next to a row; no metric is
// derived here.
//
// A finding that resolves to zero is OMITTED (a stage with no loss, no recoverable
// callers, no after-hours calls). Minimum one to render the surface at all, maximum
// four. Zero findings AND no verdict → nothing renders and the page opens on the
// money strip (E.1 degenerate).
//
// The drill is emitted as a descriptor to `onFinding`; the page owns the live scope,
// the DrillOver stack and the rail filter. Two shapes:
//   { kind: 'ids',   ids, title }        — a set of conversation_ids (a stage above)
//   { kind: 'drill', what, id, title }   — an fn_drill call (recoverable, after_hours)

const stageBy = (stages, key) => stages.find((s) => s?.key === key) || null;

// "N of M — <backend reason>". The drop count and the reach both come straight from
// the payload; the reason is the backend's own drop_label fragment. THE LABEL IS
// GUARDED: a null drop_label renders the count alone rather than the literal
// "undefined" — the same null-handling drainMarks.rowGaps / DrainLoss already apply
// (`drop_label || null`, and no em-dash when it is falsy). Never interpolate a
// possibly-null backend fragment onto a customer surface.
const lossLine = (count, reach, label) => {
  const base = reach != null ? `${fmtCount(count)} of ${fmtCount(reach)}` : `${fmtCount(count)}`;
  return label ? `${base} — ${label}` : base;
};

/**
 * Assemble the (up to four) findings from the funnel and agent-value payloads.
 * Exported for unit testing — the assembly is the part that has to be right.
 *
 * Each finding carries `resolvable`: whether its drill can actually open something.
 * A kind:'ids' finding whose conversation_ids is empty is NOT resolvable (an empty
 * drill filters the rail to nothing and opens an empty set), so the row renders
 * without a click target — the same gate DrainStage applies (`ids.length > 0`). The
 * whole-population drills (recoverable, after_hours) are always resolvable.
 */
export const buildFindings = (funnel, agentValue, modality = "voice") => {
  const stages = Array.isArray(funnel?.stages) ? funnel.stages : [];
  const findings = [];

  // 1 — Biggest loss. The stage the backend flagged is_largest_drop (never a
  // hardcoded stage, never recomputed when the flag is present), described against
  // the stage ABOVE it — the people who reached that far and were lost.
  const flagged = stages.find((s) => s?.is_largest_drop === true);
  const worst = flagged
    || stages.reduce((w, s) => (Number(s?.dropped_here) > Number(w?.dropped_here ?? -1) ? s : w), null);
  if (worst && Number(worst.dropped_here) > 0) {
    const idx = stages.indexOf(worst);
    const above = idx > 0 ? stages[idx - 1] : null;
    const reach = above ? above.count : worst.count;
    const ids = (above && above.conversation_ids) || worst.conversation_ids || [];
    findings.push({
      key: "biggest_loss",
      icon: "alert",
      tone: "leaking",
      text: lossLine(worst.dropped_here, reach, worst.drop_label),
      resolvable: ids.length > 0,
      drill: { kind: "ids", ids, title: above ? above.label : worst.label },
    });
  }

  // 2 — Rates. The people who were qualified and never got a price: the loss at the
  // quoted step, described against the qualified population it drills to (E.1:
  // stages[qualified].ids). Same "N of M — reason" form as the biggest loss — no
  // frontend-authored connective ("more" was ambiguous: more than what?).
  //
  // NOT WHEN THE RATES STEP IS ITSELF THE BIGGEST LOSS. If the largest drop is the
  // quoted step, finding 1 above already IS this line — emitting it again gives two
  // identical rows, "N of M — never got to rates", one alert-iconed and one
  // dollar-iconed (Markup #1). The two findings must be distinct; when they would
  // collide there is only one finding to show, so the biggest-loss row keeps it.
  const quoted = stageBy(stages, "quoted");
  if (quoted && Number(quoted.dropped_here) > 0 && worst?.key !== "quoted") {
    const qualified = stageBy(stages, "qualified");
    const ids = (qualified && qualified.conversation_ids) || [];
    findings.push({
      key: "rates",
      icon: "dollar",
      tone: "fixable",
      text: lossLine(quoted.dropped_here, qualified ? qualified.count : null, quoted.drop_label),
      resolvable: ids.length > 0,
      drill: { kind: "ids", ids, title: qualified ? qualified.label : quoted.label },
    });
  }

  // 3 — Recoverable. People worth money who can still be called.
  const rec = agentValue?.recoverable;
  if (rec && Number(rec.count) > 0) {
    findings.push({
      key: "recoverable",
      icon: "phone",
      tone: "good",
      text: `${fmtCount(rec.count)} people worth ${fmtCurrency(rec.value)} can still be called`,
      resolvable: true,
      drill: { kind: "drill", what: rec.drill?.what || "recoverable", id: rec.drill?.id ?? null, title: "Still reachable" },
    });
  }

  // 4 — After hours. Conversations the agent answered while the office was closed.
  // "calls" on Phone, "conversations" on Chat — visitors type, they do not call
  // (Markup #2). The count still comes straight from after_hours.calls_answered
  // (the backend field name is channel-neutral); only the noun is presentational.
  const ah = agentValue?.after_hours;
  if (ah && Number(ah.calls_answered) > 0) {
    findings.push({
      key: "after_hours",
      icon: "clock",
      tone: "good",
      text: `${fmtCount(ah.calls_answered)} ${modality === "chat" ? "conversations" : "calls"} answered while you were closed`,
      resolvable: true,
      drill: { kind: "drill", what: ah.drill?.what || "after_hours", id: ah.drill?.id ?? null, title: "After hours" },
    });
  }

  return findings;
};

const KeyFindings = ({ panelKey = "voice_verdict", say, funnel, agentValue, onFinding, loading = false }) => {
  // panelKey is voice_verdict / chat_verdict — the page's modality, used only to
  // choose channel-appropriate wording in the assembled findings (Markup #2).
  const modality = String(panelKey).startsWith("chat") ? "chat" : "voice";
  const findings = buildFindings(funnel, agentValue, modality);
  const hasVerdict = Boolean(say?.[panelKey]);

  // The findings come from RPCs; while those are in flight the surface is a shimmer.
  if (loading && findings.length === 0 && !hasVerdict) {
    return <SkeletonAlert lines={4} />;
  }
  // Nothing to say at all — no verdict and no findings. The page opens on the money
  // strip (E.1 degenerate). Render nothing rather than an empty pane.
  if (findings.length === 0 && !hasVerdict) return null;

  return (
    <div data-testid="key-findings" className="relative">
      {/* Re-fetch feedback (Phone↔Chat switch) once there is something to dim. */}
      {loading && (hasVerdict || findings.length > 0) && <RefreshingOverlay />}
      {/* Verdict first and largest, hidden entirely when absent. */}
      <VerdictBlock panelKey={panelKey} say={say} variant="verdict" />
      {findings.length > 0 && (
        <InsightSurface tone="findings" eyebrow="Key Findings">
          <ul className="space-y-0.5">
            {findings.map((f) => (
              <FindingRow
                key={f.key}
                icon={f.icon}
                tone={f.tone}
                text={f.text}
                // A finding is a control only when its drill can open something.
                // An empty conversation_ids set is not clickable (it would filter
                // the rail to nothing) — the same gate DrainStage applies.
                onClick={onFinding && f.resolvable ? () => onFinding(f.drill) : undefined}
              />
            ))}
          </ul>
        </InsightSurface>
      )}
    </div>
  );
};

export default KeyFindings;
